import type Phaser from 'phaser';
import type { CatalogueGame, EngineGameMode } from '@gp/contracts';
import type { SceneData } from './scene';

/**
 * One entry per game the client can render.
 *
 * The loaders are dynamic imports, so each game's scene, art and tuning end up
 * in its own bundle that the browser only downloads for that `gameCode`. A new
 * title adds a line here and ships a chunk; nobody playing another game pays
 * for it, and a deployment that has not shipped a game says so plainly instead
 * of opening a blank canvas.
 */

export interface GameBundle {
  sceneKey: string;
  width: number;
  height: number;
  scene: new (...args: never[]) => Phaser.Scene;
}

type GameLoader = () => Promise<GameBundle>;

const LOADERS: Record<string, GameLoader> = {
  dice: async () => {
    const { DiceScene, DICE_WIDTH, DICE_HEIGHT } = await import('./dice-scene');
    return {
      sceneKey: 'dice',
      width: DICE_WIDTH,
      height: DICE_HEIGHT,
      scene: DiceScene as unknown as GameBundle['scene'],
    };
  },
  crash: async () => {
    const { CrashScene, CRASH_WIDTH, CRASH_HEIGHT } = await import('./crash-scene');
    return {
      sceneKey: 'crash',
      width: CRASH_WIDTH,
      height: CRASH_HEIGHT,
      scene: CrashScene as unknown as GameBundle['scene'],
    };
  },
  blackjack: async () => {
    const { BlackjackScene, BLACKJACK_WIDTH, BLACKJACK_HEIGHT } = await import('./blackjack-scene');
    return {
      sceneKey: 'blackjack',
      width: BLACKJACK_WIDTH,
      height: BLACKJACK_HEIGHT,
      scene: BlackjackScene as unknown as GameBundle['scene'],
    };
  },
  'hype-spin': async () => {
    const { HypeSpinScene, HYPESPIN_WIDTH, HYPESPIN_HEIGHT } = await import('./hypespin-scene');
    return {
      sceneKey: 'hype-spin',
      width: HYPESPIN_WIDTH,
      height: HYPESPIN_HEIGHT,
      scene: HypeSpinScene as unknown as GameBundle['scene'],
    };
  },
};

/** Whether this build has a scene written for the game. */
export function hasScene(gameCode: string): boolean {
  return gameCode in LOADERS;
}

/**
 * Whether the schema-driven form can stand in for a scene.
 *
 * It can when the round belongs to the player alone: an `INSTANT` game bets
 * and settles, a `MULTI_STEP` one bets and then takes moves, and both are
 * requests this client already makes. A shared round — a crash lobby, a
 * head-to-head match — is a screen with other players on it, and a bet form
 * is not that.
 */
export function isGeneric(mode: EngineGameMode): boolean {
  return mode.kind === 'INSTANT' || mode.kind === 'MULTI_STEP';
}

/**
 * The same question asked of a catalogue tile, which knows the older
 * three-way kind rather than the engine's mode. `INSTANT` there covers both
 * of the modes above; `LOBBY` and `PEER` are the shared rounds.
 */
export function isPlayable(game: CatalogueGame): boolean {
  return hasScene(game.gameCode) || game.kind === 'INSTANT';
}

export async function loadGame(gameCode: string): Promise<GameBundle> {
  const loader = LOADERS[gameCode];
  if (!loader) {
    throw new Error(`this client build does not include the game "${gameCode}"`);
  }
  return loader();
}

export type { SceneData };
